/*  Proposta A — Operational Command Center
    Light, brutalist, Linear/Stripe-like.
    Background #FAFAFA, surface #FFFFFF, accent #0052FF.
    Display: Cabinet Grotesk · Body: IBM Plex Sans · Mono: IBM Plex Mono.
*/

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useV2Fonts, MOCK } from './_shared';
import {
  Search, Bell, ChevronDown, Plus, ArrowUpRight, ArrowDownRight,
  Activity, Wrench, AlertOctagon, ChevronRight, Command,
  LayoutGrid, Ticket as TicketIcon, BarChart3, Building2, Settings,
  Zap, ListChecks, FileText
} from 'lucide-react';

const TOKENS = {
  bg: '#FAFAFA',
  surface: '#FFFFFF',
  primary: '#000000',
  accent: '#0052FF',
  border: '#E5E5E5',
  borderStrong: '#000000',
  muted: '#737373',
  text: '#111111',
  success: '#17B877',
  warning: '#F5A623',
  danger: '#E01E5A',
  fontDisplay: '"Cabinet Grotesk", "Space Grotesk", system-ui, sans-serif',
  fontBody: '"IBM Plex Sans", system-ui, sans-serif',
  fontMono: '"IBM Plex Mono", "JetBrains Mono", monospace',
};

const PRIORITY_BADGE = {
  urgente: { bg: '#FEE2E2', fg: '#991B1B', label: 'URGENTE' },
  alta: { bg: '#FEF3C7', fg: '#92400E', label: 'ALTA' },
  media: { bg: '#E5E5E5', fg: '#404040', label: 'MEDIA' },
};

const NAV = [
  { icon: LayoutGrid, label: 'Overview', active: true },
  { icon: TicketIcon, label: 'Tickets' },
  { icon: ListChecks, label: 'Board' },
  { icon: BarChart3, label: 'Analytics' },
  { icon: Zap, label: 'Consumi' },
  { icon: Building2, label: 'Hotels' },
  { icon: FileText, label: 'Report' },
  { icon: Settings, label: 'Settings' },
];

const Sparkline = ({ data, color = TOKENS.accent, height = 28 }) => {
  const w = 120;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const step = w / (data.length - 1);
  const points = data
    .map((v, i) => `${i * step},${height - ((v - min) / range) * (height - 4) - 2}`)
    .join(' ');
  return (
    <svg width={w} height={height} className="overflow-visible">
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
      <circle cx={w} cy={height - ((data[data.length - 1] - min) / range) * (height - 4) - 2} r="2.5" fill={color} />
    </svg>
  );
};

const KpiCell = ({ label, value, delta, deltaPositive, sparkData }) => (
  <div className="px-5 py-4 border-r flex-1" style={{ borderColor: TOKENS.border }}>
    <div className="text-[10px] uppercase tracking-[0.18em] font-medium" style={{ color: TOKENS.muted, fontFamily: TOKENS.fontMono }}>
      {label}
    </div>
    <div className="flex items-end justify-between mt-2">
      <div className="text-3xl font-bold tracking-tight tabular-nums" style={{ fontFamily: TOKENS.fontDisplay, color: TOKENS.text }}>
        {value}
      </div>
      {sparkData && <Sparkline data={sparkData} />}
    </div>
    {delta && (
      <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: deltaPositive ? TOKENS.success : TOKENS.danger }}>
        {deltaPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
        {delta}
      </div>
    )}
  </div>
);

const ProposalA = () => {
  useV2Fonts();
  const [hotel, setHotel] = useState('Hotel Galleria');

  return (
    <div className="min-h-screen flex" style={{ background: TOKENS.bg, fontFamily: TOKENS.fontBody, color: TOKENS.text }} data-testid="proposal-a">
      {/* Sidebar collassata: icone + tooltip */}
      <aside className="w-14 border-r flex flex-col py-3" style={{ background: TOKENS.surface, borderColor: TOKENS.border }}>
        <div className="px-3 mb-3">
          <div className="w-8 h-8 flex items-center justify-center" style={{ background: TOKENS.primary }}>
            <span className="text-white text-xs font-bold" style={{ fontFamily: TOKENS.fontDisplay }}>HF</span>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-2">
          {NAV.map((n, i) => (
            <button
              key={i}
              className="w-10 h-10 flex items-center justify-center relative group"
              style={{ background: n.active ? '#F1F1F1' : 'transparent' }}
            >
              <n.icon className="w-4 h-4" style={{ color: n.active ? TOKENS.text : TOKENS.muted }} />
              {n.active && (
                <div className="absolute left-0 top-0 bottom-0 w-0.5" style={{ background: TOKENS.primary }} />
              )}
            </button>
          ))}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top command bar */}
        <header className="h-14 border-b flex items-center px-5 gap-4" style={{ background: TOKENS.surface, borderColor: TOKENS.border }}>
          <button className="flex items-center gap-2 px-2 py-1 border" style={{ borderColor: TOKENS.border, fontFamily: TOKENS.fontMono, fontSize: 12 }}>
            <Building2 className="w-3.5 h-3.5" />
            {hotel}
            <ChevronDown className="w-3 h-3 opacity-50" />
          </button>
          <div className="flex-1 max-w-md">
            <div className="flex items-center gap-2 px-3 py-1.5 border" style={{ borderColor: TOKENS.border }}>
              <Search className="w-3.5 h-3.5" style={{ color: TOKENS.muted }} />
              <input
                placeholder="Cerca ticket, asset, camera..."
                className="flex-1 bg-transparent text-xs outline-none"
                style={{ fontFamily: TOKENS.fontMono }}
              />
              <kbd className="text-[10px] px-1.5 py-0.5 border" style={{ borderColor: TOKENS.border, color: TOKENS.muted, fontFamily: TOKENS.fontMono }}>
                ⌘K
              </kbd>
            </div>
          </div>
          <button className="p-1.5 border" style={{ borderColor: TOKENS.border }}>
            <Bell className="w-3.5 h-3.5" />
          </button>
          <button className="px-3 py-1.5 text-xs font-medium flex items-center gap-1.5" style={{ background: TOKENS.primary, color: '#fff', fontFamily: TOKENS.fontBody }}>
            <Plus className="w-3.5 h-3.5" /> Nuovo ticket
          </button>
          <div className="w-7 h-7 border flex items-center justify-center text-[10px] font-bold" style={{ borderColor: TOKENS.border, fontFamily: TOKENS.fontMono }}>
            LB
          </div>
          <Link to="/mockup/v2" className="text-xs underline-offset-4 hover:underline" style={{ color: TOKENS.muted }}>
            ← Confronta
          </Link>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto">
          {/* Page header */}
          <div className="px-8 pt-8 pb-5 border-b" style={{ borderColor: TOKENS.border }}>
            <div className="text-[10px] uppercase tracking-[0.2em] font-medium mb-2" style={{ color: TOKENS.muted, fontFamily: TOKENS.fontMono }}>
              Overview · Live
            </div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: TOKENS.fontDisplay }}>
              Buongiorno, {MOCK.user.split(' ')[0]}.
            </h1>
            <p className="text-sm mt-1" style={{ color: TOKENS.muted }}>
              {MOCK.kpis.critical} ticket critici aperti · {MOCK.kpis.open} totali in coda · SLA {MOCK.kpis.sla}
            </p>
          </div>

          {/* KPI row */}
          <div className="flex border-b" style={{ borderColor: TOKENS.border, background: TOKENS.surface }}>
            <KpiCell label="OPEN TICKETS" value={MOCK.kpis.open} delta="+12%" deltaPositive={false} sparkData={[42, 40, 43, 45, 44, 48, 45]} />
            <KpiCell label="AVG RESOLUTION" value={MOCK.kpis.avgResolution} delta="-18m" deltaPositive sparkData={[3.1, 2.9, 2.8, 2.7, 2.6, 2.5, 2.4]} />
            <KpiCell label="SLA COMPLIANCE" value={MOCK.kpis.sla} delta="+2pp" deltaPositive sparkData={[94, 95, 96, 96, 97, 97, 98]} />
            <KpiCell label="EFF. ENERGETICA" value={MOCK.consumption.efficiency} delta="+5pt" deltaPositive sparkData={MOCK.consumption.trend12m} />
          </div>

          {/* Two columns */}
          <div className="grid grid-cols-12 gap-px" style={{ background: TOKENS.border }}>
            {/* Urgent tickets */}
            <section className="col-span-8 p-6" style={{ background: TOKENS.surface }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-medium" style={{ color: TOKENS.muted, fontFamily: TOKENS.fontMono }}>
                    PRIORITY QUEUE
                  </div>
                  <h2 className="text-lg font-semibold mt-0.5" style={{ fontFamily: TOKENS.fontDisplay }}>
                    Ticket da gestire ora
                  </h2>
                </div>
                <button className="text-xs flex items-center gap-1" style={{ color: TOKENS.accent }}>
                  Vedi tutti <ChevronRight className="w-3 h-3" />
                </button>
              </div>

              <div className="border-t" style={{ borderColor: TOKENS.border }}>
                {MOCK.urgent.map((t) => {
                  const p = PRIORITY_BADGE[t.priority];
                  return (
                    <div
                      key={t.id}
                      className="flex items-center gap-4 py-3 border-b text-sm hover:bg-neutral-50 cursor-pointer"
                      style={{ borderColor: TOKENS.border }}
                    >
                      <span className="tabular-nums w-14 text-xs" style={{ color: TOKENS.muted, fontFamily: TOKENS.fontMono }}>
                        {t.id}
                      </span>
                      <span className="flex-1 font-medium">{t.title}</span>
                      <span className="text-xs" style={{ color: TOKENS.muted, fontFamily: TOKENS.fontMono }}>
                        {t.room}
                      </span>
                      <span
                        className="text-[10px] px-2 py-0.5 font-semibold tracking-wider"
                        style={{ background: p.bg, color: p.fg, fontFamily: TOKENS.fontMono }}
                      >
                        {p.label}
                      </span>
                      <span className="w-16 text-right text-xs tabular-nums" style={{ color: TOKENS.muted, fontFamily: TOKENS.fontMono }}>
                        {t.age}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Right column: tecnici + heatmap */}
            <section className="col-span-4 p-6" style={{ background: TOKENS.surface }}>
              <div className="text-[10px] uppercase tracking-[0.18em] font-medium" style={{ color: TOKENS.muted, fontFamily: TOKENS.fontMono }}>
                TEAM LOAD
              </div>
              <h2 className="text-lg font-semibold mt-0.5 mb-4" style={{ fontFamily: TOKENS.fontDisplay }}>
                Carico tecnici
              </h2>
              {MOCK.technicians.map((t) => {
                const pct = Math.min(100, t.open * 14);
                const overload = t.open >= 7;
                return (
                  <div key={t.name} className="mb-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium">{t.name}</span>
                      <span className="tabular-nums" style={{ color: TOKENS.muted, fontFamily: TOKENS.fontMono }}>
                        {t.open} aperti · {t.today} oggi
                      </span>
                    </div>
                    <div className="h-1.5 w-full" style={{ background: '#F1F1F1' }}>
                      <div
                        className="h-full"
                        style={{
                          width: `${pct}%`,
                          background: overload ? TOKENS.danger : TOKENS.primary,
                        }}
                      />
                    </div>
                  </div>
                );
              })}

              <div className="mt-6 pt-5 border-t" style={{ borderColor: TOKENS.border }}>
                <div className="text-[10px] uppercase tracking-[0.18em] font-medium mb-2" style={{ color: TOKENS.muted, fontFamily: TOKENS.fontMono }}>
                  HOTELS · GROUP VIEW
                </div>
                {MOCK.hotels.map((h) => (
                  <div key={h.name} className="flex items-center justify-between py-1.5 text-xs">
                    <span className="font-medium">{h.name}</span>
                    <span className="tabular-nums" style={{ fontFamily: TOKENS.fontMono, color: TOKENS.muted }}>
                      {h.open} tk · eff {h.eff}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Bottom strip: consumi */}
          <section className="p-6 border-t" style={{ background: TOKENS.surface, borderColor: TOKENS.border }}>
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-medium" style={{ color: TOKENS.muted, fontFamily: TOKENS.fontMono }}>
                  ENERGY · LAST 12 MONTHS
                </div>
                <h2 className="text-lg font-semibold mt-0.5" style={{ fontFamily: TOKENS.fontDisplay }}>
                  Trend efficienza · {hotel}
                </h2>
              </div>
              <div className="flex gap-6 text-xs tabular-nums" style={{ fontFamily: TOKENS.fontMono }}>
                <div>
                  <div style={{ color: TOKENS.muted }} className="text-[10px] uppercase tracking-wider">kWh/cam</div>
                  <div className="text-xl font-bold mt-0.5" style={{ fontFamily: TOKENS.fontDisplay }}>{MOCK.consumption.kwhPerRoom}</div>
                </div>
                <div>
                  <div style={{ color: TOKENS.muted }} className="text-[10px] uppercase tracking-wider">m³/cam</div>
                  <div className="text-xl font-bold mt-0.5" style={{ fontFamily: TOKENS.fontDisplay }}>{MOCK.consumption.waterPerRoom}</div>
                </div>
                <div>
                  <div style={{ color: TOKENS.muted }} className="text-[10px] uppercase tracking-wider">Smc/cam</div>
                  <div className="text-xl font-bold mt-0.5" style={{ fontFamily: TOKENS.fontDisplay }}>{MOCK.consumption.smcPerRoom}</div>
                </div>
                <div>
                  <div style={{ color: TOKENS.muted }} className="text-[10px] uppercase tracking-wider">INDICE</div>
                  <div className="text-xl font-bold mt-0.5" style={{ color: TOKENS.success, fontFamily: TOKENS.fontDisplay }}>
                    {MOCK.consumption.efficiency}
                  </div>
                </div>
              </div>
            </div>
            <div className="h-24 flex items-end gap-1">
              {MOCK.consumption.trend12m.map((v, i) => {
                const max = Math.max(...MOCK.consumption.trend12m);
                const h = (v / max) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div style={{ background: i === 11 ? TOKENS.accent : TOKENS.primary, width: '100%', height: `${h}%`, minHeight: 4 }} />
                    <div className="text-[9px]" style={{ color: TOKENS.muted, fontFamily: TOKENS.fontMono }}>
                      {['G', 'F', 'M', 'A', 'M', 'G', 'L', 'A', 'S', 'O', 'N', 'D'][i]}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default ProposalA;
